import {
  ComponentPropsWithoutRef,
  forwardRef,
  Ref,
  useCallback,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';

import omit from 'lodash/omit';

import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import mixinSx from '@watershed/style/mixinSx';

import Button from './Button';
import CircularProgress from './CircularProgress';

export const PROGRESS_SIZES = {
  small: 14,
  medium: 16,
  large: 16,
} as const;

const spinnerStyles = {
  position: 'absolute',
  top: 'calc(50% - 8px)',
  left: '50%',
  transform: 'translateX(-50%)',
} as const;

export const ICON_SIZE = 16;

type ButtonProps = ComponentPropsWithoutRef<typeof Button>;
export type ProgressButtonProps = Pick<
  ButtonProps,
  | 'className'
  | 'variant'
  | 'color'
  | 'type'
  | 'style'
  | 'fullWidth'
  | 'disabled'
  | 'size'
  | 'startIcon'
  | 'endIcon'
  | 'href'
  | 'tabIndex'
  | 'form'
  | 'tooltip'
  | 'autoFocus'
  | 'onClick'
  | 'sx'
> & {
  isInProgress: boolean;
  progressLabel?: LocalizedString;
  onClickError?: (error: unknown) => void;
};

/**
 * `<ProgressButton>` is a wrapper around `<Button>` that makes it easier to
 * handle loading states. It requires a prop `isInProgress` to be present, which
 * controls whether to render a loading state.
 */
export default forwardRef(function ProgressButton(
  {
    onClick,
    isInProgress,
    startIcon,
    endIcon,
    progressLabel,
    onClickError,
    variant = 'contained',
    color = 'primary',
    size = 'medium',
    sx,
    ...restProps
  }: ProgressButtonProps &
    (
      | { label: ButtonProps['children'] }
      | { children: ButtonProps['children'] }
    ),
  ref: Ref<HTMLButtonElement>
) {
  const [isLoading, setIsLoading] = useState(false);
  const isExecutingRef = useRef(false);
  const realLabel = 'label' in restProps ? restProps.label : restProps.children;
  const buttonProps = omit(restProps, 'label', 'children');

  const isButtonLoading = isInProgress || isLoading;
  const circularProgressSize = PROGRESS_SIZES[size] ?? PROGRESS_SIZES.medium;

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      // Use ref for immediate synchronous check
      if (isExecutingRef.current || isInProgress) {
        return;
      }

      // Extract needed values immediately to avoid stale event object
      const { currentTarget, type, timeStamp } = event;

      // Set both state and ref to prevent race conditions
      isExecutingRef.current = true;
      setIsLoading(true);

      try {
        if (onClick) {
          // Create a safe event-like object with extracted values
          const safeEvent = {
            ...event,
            currentTarget,
            type,
            timeStamp,
            // Preserve other commonly used properties
            preventDefault: () => event.preventDefault(),
            stopPropagation: () => event.stopPropagation(),
          };

          await onClick(safeEvent as React.MouseEvent<HTMLButtonElement>);
        }
      } catch (error) {
        // Log and optionally notify parent
        console.error('ProgressButton onClick error:', error);

        // Allow parent components to handle errors
        if (onClickError) {
          onClickError(error);
        } else {
          // If no error handler provided, re-throw to maintain existing behavior
          throw error;
        }
      } finally {
        // Always reset both loading indicators
        isExecutingRef.current = false;
        setIsLoading(false);
      }
    },
    [onClick, isInProgress, onClickError]
  );

  return (
    <Button
      ref={ref}
      {...buttonProps}
      sx={mixinSx(
        {
          position: 'relative',
          cursor: isButtonLoading ? 'default' : undefined,
          // Only disable when truly loading
          pointerEvents: isButtonLoading ? 'none' : undefined,
        },
        sx
      )}
      variant={variant}
      color={color}
      size={size}
      onClick={isButtonLoading ? undefined : handleClick}
      aria-disabled={isButtonLoading || buttonProps.disabled}
      aria-live={isButtonLoading ? 'assertive' : undefined}
      aria-busy={isButtonLoading}
      startIcon={
        startIcon &&
        (isButtonLoading ? (
          <Box sx={{ width: ICON_SIZE, height: ICON_SIZE }} aria-hidden />
        ) : (
          startIcon
        ))
      }
      endIcon={
        endIcon &&
        (isButtonLoading ? (
          <Box sx={{ width: ICON_SIZE, height: ICON_SIZE }} aria-hidden />
        ) : (
          endIcon
        ))
      }
    >
      {isButtonLoading && (
        <Box component="span" aria-hidden={!isButtonLoading}>
          <Box sx={spinnerStyles}>
            <CircularProgress
              size={circularProgressSize}
              disableShrink
              thickness={5}
              color="inherit"
              sx={{ visibility: progressLabel ? 'hidden' : 'visible' }}
              data-test={TestIds.LoadingStateCircularProgress}
            />
          </Box>
        </Box>
      )}
      <Box
        component="span"
        sx={{
          position: 'relative',
          opacity: isButtonLoading && !progressLabel ? 0 : 1,
        }}
      >
        {isButtonLoading ? (progressLabel ?? realLabel) : realLabel}
      </Box>
    </Button>
  );
});
